import { useState } from 'react';
import { useNumbers, useLabelNumber, useRoutingMode, useFlowDrafts } from '../../lib/api/hooks-tms.js';
import { lastFlowKey } from './flows/panel.js';
import { useCan } from '../../lib/auth/session.js';
import { TableSkeleton, Empty, ErrorBanner, Badge, StatusBadge, SectionHead } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { useAccountRoute } from '../../lib/subroute.js';
import { CallPathShort, DestLink, nationalNumber, useRoutingLookup } from './CallPath.js';

/**
 * The account's numbers, and the inbound route each one is on.
 *
 * Read-only for routing. A number is routed by putting it on a route under
 * Inbound routes, where one decision covers every number that shares it
 * (docs/26 D1). A number on no route is flagged here because calls to it are
 * refused -- still the commonest cause of "our new number does not work".
 *
 * The name is the account's own ("Main line", "Durban"), so fifty numbers can be told apart in
 * route pickers without reading digits (0098).
 */
export function NumbersTab({ tenantId, onNavigate }: {
  tenantId: string; onNavigate?: (section: string) => void;
}) {
  const numbers = useNumbers(tenantId);
  const all = numbers.data?.data ?? [];
  const can = useCan();
  const writable = can('inboundroute:write');
  const account = useAccountRoute();
  const lookup = useRoutingLookup(tenantId);
  const [q, setQ] = useState('');
  const [show, setShow] = useState<'all' | 'routed' | 'unrouted'>('all');
  // In Flows mode a number's route is its call flow (docs/41 §4): named so, opened from here.
  const flows = useRoutingMode(tenantId).data?.mode === 'flows';
  const drafts = useFlowDrafts(tenantId, flows);
  const drafted = new Set((drafts.data?.data ?? []).flatMap((d) => (d.route_id ? [d.route_id] : [])));
  const openRoute = (routeId: string) => {
    if (flows) {
      try { sessionStorage.setItem(lastFlowKey(tenantId), routeId); } catch { /* storage may be off */ }
      onNavigate?.('inbound');
      return;
    }
    const slug = lookup.slug.get(routeId);
    if (account && slug) account.go(['inbound', slug]); else onNavigate?.('inbound');
  };
  /** A new route with this number already on it (/inbound/new/27105000372). */
  const routeIt = (e164: string) => {
    if (account) account.go(['inbound', 'new', e164.replace(/\D/g, '')]); else onNavigate?.('inbound');
  };

  const unrouted = all.filter((n) => !n.route);
  const needle = q.trim().toLowerCase();
  const digits = needle.replace(/\D/g, '');
  const rows = all
    .filter((n) => show === 'all' || (show === 'routed' ? !!n.route : !n.route))
    .filter((n) => !needle
      || (n.label ?? '').toLowerCase().includes(needle)
      || (n.route?.name ?? '').toLowerCase().includes(needle)
      || (digits.length >= 2 && (n.e164.replace(/\D/g, '').includes(digits)
        || nationalNumber(n.e164).replace(/\D/g, '').includes(digits))))
    // Numbers on no route first: calls to them are refused, so they are the ones to see.
    .sort((a, b) => Number(!!a.route) - Number(!!b.route) || a.e164.localeCompare(b.e164));
  const counts = { all: all.length, routed: all.length - unrouted.length, unrouted: unrouted.length };

  return (
    <>
      <SectionHead title="Assigned numbers"
        sub={<>
          {all.length} {all.length === 1 ? 'number' : 'numbers'}
          {unrouted.length > 0 ? ` · ${unrouted.length} on no route` : ''}
        </>}
        action={onNavigate && (
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => onNavigate('inbound')}>
            Inbound routes
          </button>
        )} />

      {numbers.error && <ErrorBanner error={numbers.error} />}

      {/* The one fault worth a banner: calls to these numbers are refused. */}
      {unrouted.length > 0 && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            <span className="arc-mono">{unrouted.map((n) => nationalNumber(n.e164)).join(', ')}</span>{' '}
            {unrouted.length === 1 ? 'is on no route, so calls to it are' : 'are on no route, so calls to them are'} refused.
          </span>
          {writable && !flows && (
            <button type="button" className="arc-btn arc-btn-sm" style={{ marginLeft: 'auto' }}
              onClick={() => routeIt(unrouted[0]!.e164)}>
              Route {unrouted.length === 1 ? 'it' : nationalNumber(unrouted[0]!.e164)}
            </button>
          )}
        </div>
      )}

      <div className="arc-card">
        <div className="arc-toolbar">
          <div className="arc-search">
            <Icon name="search" size={14} />
            <input className="arc-input" placeholder="Number or name" value={q}
              onChange={(e) => setQ(e.target.value)} aria-label="Search numbers" />
          </div>
          <div className="arc-row" style={{ gap: 4, flexWrap: 'wrap' }} role="group" aria-label="Show">
            {([['all', 'All'], ['routed', flows ? 'In a call flow' : 'On a route'], ['unrouted', 'No route']] as const).map(([k, label]) => (
              <button key={k} type="button" aria-pressed={show === k}
                className={`arc-btn arc-btn-sm ${show === k ? 'arc-btn-primary' : 'arc-btn-ghost'}`}
                onClick={() => setShow(k)}>
                {label}<span className="arc-seg-count">{counts[k]}</span>
              </button>
            ))}
          </div>
          <div className="arc-spacer" />
          <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
            {rows.length === all.length ? all.length : `${rows.length} of ${all.length}`} shown
          </span>
        </div>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <colgroup>
              <col style={{ width: 200 }} /><col style={{ width: 160 }} /><col style={{ width: 200 }} /><col /><col style={{ width: 100 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Number</th><th>Name</th><th>{flows ? 'Call flow' : 'Inbound route'}</th><th>Where calls go now</th><th />
              </tr>
            </thead>
            {numbers.data === undefined ? <TableSkeleton query={numbers} cols={5} /> : (
              <tbody>
                {rows.map((n) => {
                  const route = n.route ? lookup.routes.find((r) => r.id === n.route!.id) : undefined;
                  const slug = n.route ? lookup.slug.get(n.route.id) : undefined;
                  return (
                    <tr key={n.id}>
                      <td>
                        <div className="arc-stack" style={{ gap: 1, minWidth: 0 }} title={n.number_type.replace(/_/g, ' ')}>
                          <span className="arc-row" style={{ gap: 6 }}>
                            <span className="arc-num-national">{nationalNumber(n.e164)}</span>
                            {n.status !== 'active' && <StatusBadge status={n.status} />}
                          </span>
                          <span className="arc-subtle" style={{ fontSize: 11.5 }}>
                            {n.e164}{n.carrier_name ? ` · ${n.carrier_name}` : ''}
                          </span>
                        </div>
                      </td>
                      <td>
                        <NumberName tenantId={tenantId} id={n.id} label={n.label ?? null}
                          number={nationalNumber(n.e164)} writable={writable} />
                      </td>
                      <td>
                        {n.route ? (
                          <span className="arc-row" style={{ gap: 6, minWidth: 0 }}>
                            <DestLink kind="" name={n.route.name} rest={!flows && slug ? ['inbound', slug] : null} />
                            {!n.route.is_enabled && <Badge tone="warn">off</Badge>}
                            {n.route.is_enabled && route && route.hours.open_now !== null && (
                              <Badge tone={route.hours.open_now ? 'ok' : 'muted'}>
                                <span className="arc-dot" aria-hidden="true" />{route.hours.open_now ? 'open' : 'closed'}
                              </Badge>
                            )}
                            {flows && drafted.has(n.route.id) && <Badge tone="warn"><span className="arc-dot" />draft</Badge>}
                          </span>
                        ) : (
                          <span className="arc-badge arc-badge-warn">
                            <Icon name="warning" size={10} /> no route
                          </span>
                        )}
                      </td>
                      <td>
                        {route ? <CallPathShort route={route} L={lookup} />
                          : n.route ? <span className="arc-skeleton" style={{ display: 'block', height: 14, width: '60%' }} />
                            : <span className="arc-muted" style={{ fontSize: 12.5 }}>Calls are refused</span>}
                      </td>
                      <td className="arc-td-right">
                        {n.route ? (
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-sm"
                            title={`Open ${flows ? 'the call flow' : 'the route'} ${n.route.name}`}
                            aria-label={`Open ${flows ? 'the call flow' : 'the route'} ${n.route.name}`}
                            onClick={() => openRoute(n.route!.id)}>
                            <Icon name="chevronRight" size={15} />
                          </button>
                        ) : writable && !flows && (
                          <button type="button" className="arc-btn arc-btn-sm" onClick={() => routeIt(n.e164)}>Route it</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>
        {numbers.data !== undefined && all.length === 0 && (
          <Empty icon="hash" title="No numbers assigned"
            hint="Assign a DID from platform inventory to give this account an inbound number." />
        )}
        {numbers.data !== undefined && all.length > 0 && rows.length === 0 && (
          <Empty icon="search" title="No number matches" hint="Clear the search or choose All." />
        )}
      </div>
      <p className="arc-hint" style={{ marginTop: 10 }}>
        A number is routed on its {flows ? 'call flow' : 'inbound route'}. Every underlined name opens what it names.
      </p>
    </>
  );
}

/** A number's name, edited where it is shown: click, type, Enter. Escape leaves it as it was. */
function NumberName({ tenantId, id, label, number, writable }: {
  tenantId: string; id: string; label: string | null;
  /** As shown, for the button's name: "Rename 010 500 0371". */
  number: string;
  writable: boolean;
}) {
  const save = useLabelNumber(tenantId);
  const [draft, setDraft] = useState<string | null>(null);

  if (!writable) return label ? <span>{label}</span> : <span className="arc-subtle">—</span>;

  if (draft === null) {
    return (
      <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => setDraft(label ?? '')}
        title={label ? `Rename ${number}` : `Name ${number}`} style={{ marginLeft: -8 }}>
        {label ?? <span className="arc-subtle">Add name</span>}
      </button>
    );
  }

  const commit = () => {
    const next = draft.trim() || null;
    setDraft(null);
    if (next !== label) save.mutate({ id, label: next });
  };

  return (
    <input
      className="arc-input" style={{ maxWidth: 200, height: 28 }} autoFocus maxLength={60}
      placeholder="Main line" value={draft} aria-label="Number name"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') setDraft(null);
      }}
    />
  );
}
